import React, { useEffect, useMemo, useState } from "react";
import BaseModal from "@reusables/Modals/BaseModal";
import { Customer } from "@/types/general";
import { LineSchema } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/types";
import AddProductDraftForm from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/components/AddProductDraftForm";
import {
    AddProductLineMutationPayload,
    CreateCarryOver,
    DEFAULT_CREATE_CARRY_OVER,
    DraftFinishResult,
    DraftMode,
} from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/addProductDraftShared";

export interface AddProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLineMutation: (newOrder: AddProductLineMutationPayload) => void;
    customer?: Customer.Root;
    selectedLine?: LineSchema;
}

export default function AddProductModal({
    isOpen,
    onClose,
    onLineMutation,
    customer,
    selectedLine,
}: AddProductModalProps): JSX.Element {
    // The modal shell stays mounted, but each logical line draft gets its own keyed form subtree.
    // That keeps the lifetime of RHF state aligned with one record instead of one long-lived modal.
    const [draftSessionId, setDraftSessionId] = useState(0);
    const [createCarryOver, setCreateCarryOver] =
        useState<CreateCarryOver>(DEFAULT_CREATE_CARRY_OVER);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const draftMode: DraftMode = selectedLine ? "edit" : "create";
    const draftSessionKey = useMemo(() => {
        if (draftMode === "edit") {
            return `edit:${selectedLine?.id}:${draftSessionId}`;
        }

        return `create:${draftSessionId}`;
    }, [draftMode, draftSessionId, selectedLine?.id]);

    useEffect(() => {
        if (isOpen) return;

        // Closing the shell ends the current draft session completely. The next create open should
        // start from remembered defaults only, not from the last add-more carry-over state.
        setCreateCarryOver(DEFAULT_CREATE_CARRY_OVER);
        setIsSubmitting(false);
    }, [isOpen]);

    const handleClose = () => {
        setCreateCarryOver(DEFAULT_CREATE_CARRY_OVER);
        setIsSubmitting(false);
        onClose();
    };

    const handleDraftFinished = (result: DraftFinishResult) => {
        if (result.action === "continue") {
            // Bumping the draft-session id remounts the form subtree so the next record gets a
            // brand-new RHF instance and brand-new child component state.
            setCreateCarryOver(result.carryOver);
            setDraftSessionId((value) => value + 1);
            return;
        }

        handleClose();
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            isLoading={isSubmitting}
            keepMounted={false}
            width={900}
            padding="56px"
        >
            {/* Keep the draft form in the dialog tree for the full close transition so the modal
                paper keeps its content height while the loading overlay is visible. MUI will
                unmount the dialog subtree after the exit transition because keepMounted=false. */}
            <AddProductDraftForm
                key={draftSessionKey}
                mode={draftMode}
                selectedLine={selectedLine}
                customer={customer}
                initialCarryOver={createCarryOver}
                onCommitLine={onLineMutation}
                onDraftFinished={handleDraftFinished}
                onSubmittingChange={setIsSubmitting}
            />
        </BaseModal>
    );
}
